import { Alert, Center, Stack, Text } from '@mantine/core';
import { Component, ErrorInfo, ReactNode } from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';

import { isChunkLoadError, handleChunkLoadError } from '@/utils/chunkLoadRecovery.ts';
import { reportException } from '@/services/sentry.ts';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';

type Props = {
  children: ReactNode;
  fullScreen?: boolean;
  resetKey?: string;
  fallback?: (content: ReactNode) => ReactNode;
};

type State = {
  hasError: boolean;
  error: unknown;
};

export default class LazyLoadErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    if (handleChunkLoadError(error, errorInfo.componentStack)) return;

    reportException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    const isLazyLoadFailure = isChunkLoadError(this.state.error);

    const content = (
      <Center
        h={this.props.fullScreen ? '100vh' : '100%'}
        w={this.props.fullScreen ? '100vw' : '100%'}
        p="md"
        pos={this.props.fullScreen ? 'fixed' : undefined}
        inset={this.props.fullScreen ? 0 : undefined}
        bg={this.props.fullScreen ? 'rgba(255, 255, 255, 0.92)' : undefined}
        style={{ zIndex: this.props.fullScreen ? 1000 : undefined }}
      >
        <Alert
          color="red"
          radius="md"
          icon={<IconAlertTriangle size={18} />}
          title={isLazyLoadFailure ? 'Unable to load the latest app code' : 'Something went wrong'}
        >
          <Stack gap="sm">
            <Text size="sm">
              {isLazyLoadFailure
                ? 'Refresh the page to retry loading this view.'
                : 'Refresh the page and try again.'}
            </Text>
            <StyledButton size="sm" variant="outline" onClick={() => window.location.reload()}>
              Refresh
            </StyledButton>
          </Stack>
        </Alert>
      </Center>
    );
    return this.props.fallback ? this.props.fallback(content) : content;
  }
}
